/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.14.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { icons } from '@assets/index';
import { LinkBlock, TextBlockModal, TextBlockStatus } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { ETextBlockStatus } from '@components/common/TextBlockStatus/textBlockStatusTypes';
import type { ISimpleTableColumns, ISimpleTableData } from '@components/SimpleTable/SimpleTable';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalAccounts,
} from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type { IWizardAdditionalAccountsTranslation } from '@modules/internationalization/types/wizards/additionalAccounts';
import { WizardStepConfirmationRemovedItemStyled } from '@modules/wizard/components/styled/WizardConfirmationStyled';
import WizardConfirmationStep from '@modules/wizard/components/WizardConfirmationStep';
import type { IWizardSummaryParam } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportStorageSaveAction } from '@modules/wizard/wizardActions';
import type { IRootReducer } from '@reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';

import { makeSelectFeatureEnabled } from '../../PageAuth/selectors/authSelectors';
import type { TWizardAdditionalAccountsSetParamsPayload } from '../types/wizardAdditionalAccountsActionTypes';
import {
    wizardAdditionalAccountsSetParamsAction,
    wizardAdditionalAccountsStartValidateAction,
} from '../wizardAdditionalAccountsActions';
import {
    EWizardAdditionalAccountsFieldList,
    EWizardAdditionalAccountsSteps,
} from '../wizardAdditionalAccountsConstant';
import type {
    IWizardAdditionalAccountsOrderSubscriber,
    IWizardAdditionalAccountsReducer,
} from '../wizardAdditionalAccountsReducer';
import {
    makeSelectWizardAdditionalAccountsIsOnlineCheckProceeded,
    makeSelectWizardAdditionalAccountsOrderResult,
    makeSelectWizardAdditionalAccountsRemovedSubscribers,
    makeSelectWizardAdditionalAccountsSelectedTypes,
} from '../wizardAdditionalAccountsSelectors';

const WizardAdditionalAccountsConfirmation = (props: IConnectedProps & IDispatchProps) => {
    const {
        isOnlineCheckProceeded,
        orderResult,
        removedSubscribers,
        selected,
        translation,
        translationCommon,

        saveCurrentStepState,
        setParams,
        startValidate,
        storageSave,
    } = props;
    const stepTranslations = translation[EWizardAdditionalAccountsSteps.StepConfirmation];
    const isCheckPersonalAccountBalanceBeforeClosingEnabled = 
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CHECK_PERSONAL_ACCOUNT_BALANCE_BEFORE_CLOSING));

    const [tableData, setTableData] = useState<ISimpleTableData[]>([]);
    const [tableColumns, setTableColumns] = useState<ISimpleTableColumns[]>([]);
    const [summaryParams, setSummaryParams] = useState<IWizardSummaryParam[]>([]);
    const [subsCount, setSubsCount] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    const summaryInstructionHtml = useMemo(
        () => { 
            if (isCheckPersonalAccountBalanceBeforeClosingEnabled) 
                {
                    return selected.isActivateAdditionalAccounts
                    ? ''
                    : translation.wizardSummary.deactivationGetRemainInstruction
                }
        }
        ,[
            isCheckPersonalAccountBalanceBeforeClosingEnabled, 
            selected.isActivateAdditionalAccounts, 
            translation.wizardSummary.deactivationGetRemainInstruction,
        ],
    );

    const removeHandler = useCallback(
        (id: number) => {
            setParams({
                removedSubscribers: removedSubscribers.concat(id),
            });
        },
        [setParams, removedSubscribers],
    );

    const recoverHandler = useCallback(
        (id: number) => {
            setParams({
                removedSubscribers: [...removedSubscribers.filter((item) => item !== id)],
            });
        },
        [setParams, removedSubscribers],
    );

    const getItemStatusError = useCallback(
        (item: IWizardAdditionalAccountsOrderSubscriber) => {
            let text = '';
            if (item.personalAccount && item.personalAccount.errors?.length > 0) {
                text += `<h3>${
                    selected.isActivateAdditionalAccounts
                        ? stepTranslations.orderErrors.activatePersonalAccountError
                        : stepTranslations.orderErrors.deactivatePersonalAccountError
                }</h3>`;
                item.personalAccount.errors.forEach((error) => {
                    text += `<p>${error.errorMessage}</p>`;
                });
            }
            if (item.contentAccount && item.contentAccount.errors?.length > 0) {
                text += `<h3>${
                    selected.isActivateAdditionalAccounts
                        ? stepTranslations.orderErrors.activateContentAccountError
                        : stepTranslations.orderErrors.deactivateContentAccountError
                }</h3>`;
                item.contentAccount.errors.forEach((error) => {
                    text += `<p>${error.errorMessage}</p>`;
                });
            }
            return text;
        },
        [selected.isActivateAdditionalAccounts, stepTranslations.orderErrors],
    );

    const getItemStatus = useCallback(
        (item: IWizardAdditionalAccountsOrderSubscriber) => {
            return (
                <TextBlockModal disabled={item.status === ETextBlockStatus.OK} text={getItemStatusError(item)}>
                    <TextBlockStatus status={item.status}>
                        <LinkBlock disabled={item.status === ETextBlockStatus.OK}>
                            {selected.isActivateAdditionalAccounts
                                ? stepTranslations.activateStatus[item.status]
                                : stepTranslations.deactivateStatus[item.status]}
                        </LinkBlock>
                    </TextBlockStatus>
                </TextBlockModal>
            );
        },
        [getItemStatusError, selected.isActivateAdditionalAccounts, stepTranslations],
    );

    /**
     * Preparing table data
     */
    useEffect(() => {
        setTableData(
            orderResult.orderSubs.map((item: IWizardAdditionalAccountsOrderSubscriber) => ({
                id: item.subsId,
                msisdn: (
                    <LinkBlock url={RoutePath.subscriberId.replace(CLIENT_ID, item.subsId.toString())}>
                        {item.msisdn}
                    </LinkBlock>
                ),
                ...(isOnlineCheckProceeded
                    ? {
                          cost: (
                              <div>
                                  {item.charge || 0} {translationCommon.currency}
                              </div>
                          ),
                          status: getItemStatus(item),
                      }
                    : {}),
                delete: (
                    <div>
                        {removedSubscribers.includes(item.subsId) && (
                            <WizardStepConfirmationRemovedItemStyled style={{ position: 'absolute', left: 0, top: 0 }}>
                                <span>{stepTranslations.removedFromList}</span>
                                <LinkBlock onClick={() => recoverHandler(item.subsId)}>
                                    {stepTranslations.recoveryToList}
                                </LinkBlock>
                            </WizardStepConfirmationRemovedItemStyled>
                        )}
                        <ImgStyled
                            src={icons.removeSmallOpacityIcon}
                            width="24px"
                            onClick={() => removeHandler(item.subsId)}
                        />
                    </div>
                ),
            })),
        );
    }, [
        getItemStatus,
        isOnlineCheckProceeded,
        orderResult,
        recoverHandler,
        removeHandler,
        removedSubscribers,
        stepTranslations,
        translationCommon,
    ]);

    /**
     * Preparing table columns
     */
    useEffect(() => {
        setTableColumns([
            {
                id: EWizardAdditionalAccountsFieldList.MSISDN,
                title: stepTranslations.subscriberActivationListTitles[EWizardAdditionalAccountsFieldList.MSISDN],
                width: 140,
            },
            ...(isOnlineCheckProceeded
                ? [
                      {
                          id: EWizardAdditionalAccountsFieldList.COST,
                          title: stepTranslations.subscriberActivationListTitles[
                              EWizardAdditionalAccountsFieldList.COST
                          ],
                          width: 120,
                      },
                      {
                          id: EWizardAdditionalAccountsFieldList.STATUS,
                          title: stepTranslations.subscriberActivationListTitles[
                              EWizardAdditionalAccountsFieldList.STATUS
                          ],
                      },
                  ]
                : []),
            {
                id: EWizardAdditionalAccountsFieldList.DELETE,
                title: '',
                align: 'right',
                width: 40,
            },
        ]);
    }, [isOnlineCheckProceeded, stepTranslations.subscriberActivationListTitles]);

    /**
     * Preparing summary params
     */
    useEffect(() => {
        setSummaryParams([
            {
                title: selected.isActivateAdditionalAccounts
                    ? translation.wizardSummary.activationAdditionalAccounts
                    : translation.wizardSummary.deactivationAdditionalAccounts,
                values: selected.isActivateAdditionalAccounts
                    ? [
                          selected.isActivatePersonalAccount ? translation.personalAccount : '',
                          selected.isActivateContentAccount ? translation.contentAccount : '',
                      ]
                    : [
                          selected.isDeactivatePersonalAccount ? translation.personalAccount : '',
                          selected.isDeactivateContentAccount ? translation.contentAccount : '',
                      ],
            },
            {
                title: selected.isActivateAdditionalAccounts
                    ? translation.wizardSummary.activationSubscribersAmount
                    : translation.wizardSummary.deactivationSubscribersAmount,
                values: [`${subsCount}`],
            },
            ...(isOnlineCheckProceeded
                ? [
                      {
                          title: selected.isActivateAdditionalAccounts
                              ? translation.wizardSummary.activationTotalCost
                              : translation.wizardSummary.deactivationTotalCost,
                          values: [`${totalPrice} ${translationCommon.currency}`],
                      },
                  ]
                : []),
        ]);
    }, [isOnlineCheckProceeded, selected, subsCount, totalPrice, translation, translationCommon.currency]);

    /**
     * Calc count and totalCost
     */
    useEffect(() => {
        const listFiltered = orderResult.orderSubs.filter((item) => !removedSubscribers.includes(item.subsId));
        setSubsCount(listFiltered.length);
        saveCurrentStepState({
            subscriberIds: listFiltered.map((item) => item.subsId),
        });
        setTotalPrice(
            listFiltered.reduce((acc, cur) => {
                return acc + (cur.charge || 0);
            }, 0),
        );
        startValidate();
    }, [orderResult, removedSubscribers, saveCurrentStepState, startValidate]);

    return (
        <WizardConfirmationStep
            isOnlineCheckProceeded={isOnlineCheckProceeded}
            isReportSubscribers={true}
            onlineCheckInfo={stepTranslations.onlineCheck}
            startValidate={startValidate}
            storageSave={storageSave}
            summaryInstructionHtml={summaryInstructionHtml}
            summaryParams={summaryParams}
            summaryTitle={
                selected.isActivateAdditionalAccounts
                    ? translation.wizardSummary.activationTitle
                    : translation.wizardSummary.deactivationTitle
            }
            tableColumns={tableColumns}
            tableData={tableData}
            wizardReport={translation.wizardReport}
        />
    );
};

export interface IConnectedProps
    extends Pick<
        IWizardAdditionalAccountsReducer,
        'isOnlineCheckProceeded' | 'orderResult' | 'removedSubscribers' | 'selected'
    > {
    translation: IWizardAdditionalAccountsTranslation;
    translationCommon: ICommonTranslation;
}

interface IDispatchProps {
    saveCurrentStepState: (payload: TWizardAdditionalAccountsSetParamsPayload) => void;
    setParams: (payload: TWizardAdditionalAccountsSetParamsPayload) => void;
    startValidate: () => void;
    storageSave: () => void;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isOnlineCheckProceeded: makeSelectWizardAdditionalAccountsIsOnlineCheckProceeded(),
    orderResult: makeSelectWizardAdditionalAccountsOrderResult(),
    removedSubscribers: makeSelectWizardAdditionalAccountsRemovedSubscribers(),
    selected: makeSelectWizardAdditionalAccountsSelectedTypes(),
    translation: makeSelectTranslationWizardAdditionalAccounts(),
    translationCommon: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    saveCurrentStepState: (payload: TWizardAdditionalAccountsSetParamsPayload) =>
        dispatch(wizardAdditionalAccountsSetParamsAction(payload)),
    setParams: (payload) => dispatch(wizardAdditionalAccountsSetParamsAction(payload)),
    startValidate: () => dispatch(wizardAdditionalAccountsStartValidateAction()),
    storageSave: () => dispatch(wizardReportStorageSaveAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalAccountsConfirmation);
